import React from "react";
import { useAppContext } from "../../AppProvider";
import { ButtonUtility } from "../../Components/common/Buttons/ButtonUtility";
import { ButtonDelete } from "../../Components/common/Buttons/ButtonDelete";
import {
  useCollectionIndexContext,
  useCollectionDeleteContext,
} from "./providers";

/* ===========================================================================
 * COMPONENT: CollectionDelete
 * =========================================================================== */

/**
 * @component CollectionDelete
 *
 * A confirmation modal content component for deleting collections.
 * It uses the `CollectionIndexContext` for generic UI settings and the
 * `CollectionDeleteContext` to manage the actual deletion logic for one
 * or more items.
 *
 * @returns {JSX.Element} A UI fragment with deletion warnings and action buttons.
 */
const CollectionDelete: React.FC = () => {
  /** Access global dictionary for localized strings and warning messages. */
  const { dictionary } = useAppContext();

  /** Access index-level modal settings (e.g., custom titles). */
  const { modalConfig } = useCollectionIndexContext();

  /** * Access specific deletion state and functions:
   * - data: The list of IDs targeted for deletion.
   * - callback: Function to execute after a successful operation (usually closes the modal).
   * - loader: Boolean for tracking the delete request progress.
   * - deleteCollections: The execution trigger for the batch delete.
   */
  const { data, callback, loader, deleteCollections } =
    useCollectionDeleteContext();

  /* ---------------------------------------------------------------------------
   * RENDER
   * ------------------------------------------------------------------------- */

  return (
    <React.Fragment>
      {/* Header and Confirmation Message */}
      <div className="row mb-3">
        <div className="col">
          <h3 className="text-center">
            {modalConfig.title || dictionary.actions.delete}
          </h3>
          <label className="label-5 text-center">
            {/* Dynamic message: switches based on single vs batch deletion */}
            {data?.length && data.length > 1
              ? dictionary.messages.ask_to_delete_selected
              : dictionary.messages.ask_to_delete}
          </label>
        </div>
      </div>

      {/* Action Buttons Group */}
      <div className="d-flex">
        {/* Cancel Button: aborts the action and triggers the cleanup callback */}
        <ButtonUtility
          className="w-50 me-2"
          onClick={() => {
            if (callback) {
              callback();
            }
          }}
        >
          {dictionary.actions.cancel}
        </ButtonUtility>

        {/* Delete Button: executes the deletion logic */}
        <ButtonDelete
          className="w-50"
          onClick={() => deleteCollections()}
          disabled={loader}
          loading={loader}
        >
          {dictionary.actions.delete}
        </ButtonDelete>
      </div>
    </React.Fragment>
  );
};

export default CollectionDelete;
